import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import { InputTaskCategory } from 'interfaces/IInputTask';
import type { IInputStep, IInputStepRateCost } from 'interfaces/IInputStep';
import { CASHFLOW_PERIOD_MONTH } from 'constants/uiConstants';
import type {
  CashflowYearMode,
  SummaryEntityCategory,
} from '../../../../../redux/slices/InsightsSlice';
import {
  buildCashflowPeriods,
  resolveCashflowPeriodIndex,
} from '../cashflow/cashflowUtils';
import { resolveAreaHa, resolveEffectiveRate } from '../insightUtils';
import { COST_STEP_TYPES } from 'pages/FarmsPage/Workspace/Insights/costCategories';
import { resolveInputsAreaHa, STEP_TYPE_TO_SUMMARY_ENTITY } from './summary2Utils';

export interface SpendLineItem {
  label: string;
  /** ISO date string this step was dated to (the same date used to bucket it into its month). */
  date: string;
  cost: number;
}

export interface MonthlySpendPoint {
  /** 0-based position within the year window (0 = first month of the calendar / financial year). */
  index: number;
  calendarYear: number;
  /** 0 = Jan .. 11 = Dec */
  monthIndex: number;
  /** e.g. "Jul 26" — carries the year, so a window crossing a year boundary is never ambiguous. */
  monthLabel: string;
  planCumulative: number;
  /** Cumulative Actual spend through this month — stays flat past the last month with any recorded Actual step (nothing to add, not a drop to zero). */
  actualCumulative: number;
  /**
   * Projected cumulative spend — Actual to date, then the Plan still to come
   * (dated today or later, after the last Actual step). null before the last
   * Actual month, and everywhere when there's no Actual data to start from.
   */
  projectionCumulative: number | null;
  /** Plan-task cost steps dated within this month — what made the Plan line move here. */
  planItems: SpendLineItem[];
  /** Actual-task cost steps dated within this month. */
  actualItems: SpendLineItem[];
}

/** The year window a timeline covers — the same calendar / financial year choice as Cashflow. */
export interface SpendTimelineWindow {
  year: number;
  yearMode: CashflowYearMode;
}

export interface SpendTimeline {
  points: MonthlySpendPoint[];
  /** Window index of the last month with at least one dated Actual-task cost step. null when no Actual spend is recorded in the window. */
  lastActualIndex: number | null;
  /** Exact date of the most recent dated Actual-task cost step — null when none. More precise than lastActualIndex for an "as of" label. */
  lastActualDate: string | null;
  planTotal: number;
  /** Cumulative Plan spend through lastActualIndex — the figure Actual-to-date should be measured against, not the full-year Plan total. Null when there's no Actual data to anchor it to. */
  planToDate: number | null;
  actualToDate: number | null;
  /** Projected spend at the end of the window — null when there's no Actual data to project from. */
  projectedTotal: number | null;
  /** Dated cost steps that fall outside the year window, so aren't on this timeline. */
  outOfWindowCount: number;
  /** Summed area (ha) of the inputs behind this timeline — divides every $ figure for the $/ha display mode. */
  areaHa: number;
}

/** The date a step is bucketed by, via the same fallback chain computeSummary.ts uses for its monthly columns: step → task → input end date. Returns null when no date is resolvable — such a step can't be placed on a timeline. */
const resolveStepDate = (
  step: IInputStep,
  taskEndDate: string | null,
  inputEndDate: string | null
): string | null => {
  const dateStr = step.endDate ?? taskEndDate ?? inputEndDate;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return dateStr;
};

/** Start of the given day in UTC — Plan dated on or after this is "still to come". */
const startOfUtcDay = (date: Date): number =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/**
 * Monthly cumulative Plan vs Actual spend across one calendar or financial
 * year window — grouped by year and month, so months from different years
 * are never added together (a step outside the window is counted in
 * outOfWindowCount instead). The Plan line covers the full window; the
 * Actual line runs through the last month with recorded Actual data; the
 * projection continues from that point with the Plan still to come.
 * `categoryFilter` scopes to one input category, same as
 * computePlanVsActual.ts.
 *
 * Callers are expected to have already narrowed `inputs` to a single crop
 * (or a small group sharing one crop) — this function itself is
 * crop-agnostic and just sums whatever inputs it's given.
 */
export const computeSpendTimeline = (
  inputs: IInput[],
  gUnits: GUnit[],
  categoryFilter: 'all' | SummaryEntityCategory,
  window: SpendTimelineWindow,
  today: Date = new Date()
): SpendTimeline => {
  const periods = buildCashflowPeriods(
    window.year,
    window.yearMode,
    CASHFLOW_PERIOD_MONTH
  );
  const planMonthly = new Array<number>(periods.length).fill(0);
  const actualMonthly = new Array<number>(periods.length).fill(0);
  const planItemsByMonth: SpendLineItem[][] = periods.map(() => []);
  const actualItemsByMonth: SpendLineItem[][] = periods.map(() => []);
  let lastActualDate: string | null = null;
  let lastActualTime = -Infinity;
  let outOfWindowCount = 0;

  for (const input of inputs) {
    for (const task of input.tasks) {
      const isPlan = task.category === InputTaskCategory.Plan;
      const isActual = task.category === InputTaskCategory.Actual;
      if (!isPlan && !isActual) continue;

      for (const step of task.steps) {
        if (!COST_STEP_TYPES.has(step.stepType)) continue;
        if (
          categoryFilter !== 'all' &&
          STEP_TYPE_TO_SUMMARY_ENTITY[step.stepType] !== categoryFilter
        )
          continue;

        const dateStr = resolveStepDate(step, task.endDate, input.endDate);
        if (dateStr === null) continue;

        const areaHa = resolveAreaHa(step, task, input, gUnits);
        if (areaHa <= 0) continue;

        const s = step as unknown as IInputStepRateCost;
        const cost = s.cost ?? 0;
        const effectiveRate = resolveEffectiveRate(step, task, gUnits);
        if (effectiveRate === null || effectiveRate <= 0 || cost <= 0) continue;

        const periodIndex = resolveCashflowPeriodIndex(dateStr, periods);
        if (periodIndex === 'noDate') continue;
        if (periodIndex === 'outOfWindow') {
          outOfWindowCount++;
          continue;
        }

        const stepCost = effectiveRate * cost * areaHa;
        const item: SpendLineItem = {
          label: step.item || 'Unnamed item',
          date: dateStr,
          cost: stepCost,
        };

        if (isPlan) {
          planMonthly[periodIndex] += stepCost;
          planItemsByMonth[periodIndex].push(item);
        } else {
          actualMonthly[periodIndex] += stepCost;
          actualItemsByMonth[periodIndex].push(item);
          const time = new Date(dateStr).getTime();
          if (time > lastActualTime) {
            lastActualTime = time;
            lastActualDate = dateStr;
          }
        }
      }
    }
  }

  const lastActualIndex =
    lastActualDate === null
      ? null
      : (resolveCashflowPeriodIndex(lastActualDate, periods) as number);

  // Plan still to come: dated today or later, and after the last Actual step
  // (anything earlier has either happened — and is in Actual — or was missed).
  const projectFrom = Math.max(startOfUtcDay(today), lastActualTime + 1);
  const remainingPlanMonthly = planItemsByMonth.map((items) =>
    items
      .filter((item) => new Date(item.date).getTime() >= projectFrom)
      .reduce((sum, item) => sum + item.cost, 0)
  );

  const points: MonthlySpendPoint[] = [];
  let planRunning = 0;
  let actualRunning = 0;
  let projectionRunning = 0;
  periods.forEach((period, i) => {
    const { calendarYear, monthIndex } = period.months[0];
    planRunning += planMonthly[i];
    actualRunning += actualMonthly[i];
    // The projection starts exactly on the last Actual point; Plan still to
    // come later in that same month is carried into the next point.
    let projectionCumulative: number | null = null;
    if (lastActualIndex !== null && i === lastActualIndex) {
      projectionRunning = actualRunning;
      projectionCumulative = projectionRunning;
    } else if (lastActualIndex !== null && i > lastActualIndex) {
      projectionRunning +=
        remainingPlanMonthly[i] +
        (i === lastActualIndex + 1 ? remainingPlanMonthly[lastActualIndex] : 0);
      projectionCumulative = projectionRunning;
    }
    points.push({
      index: i,
      calendarYear,
      monthIndex,
      monthLabel: `${period.label} ${String(calendarYear).slice(-2)}`,
      planCumulative: planRunning,
      actualCumulative: actualRunning,
      projectionCumulative,
      planItems: planItemsByMonth[i],
      actualItems: actualItemsByMonth[i],
    });
  });

  return {
    points,
    lastActualIndex,
    lastActualDate,
    planTotal: planRunning,
    planToDate:
      lastActualIndex === null ? null : points[lastActualIndex].planCumulative,
    actualToDate:
      lastActualIndex === null
        ? null
        : points[lastActualIndex].actualCumulative,
    projectedTotal:
      lastActualIndex === null
        ? null
        : lastActualIndex === points.length - 1
          ? projectionRunning + remainingPlanMonthly[lastActualIndex]
          : projectionRunning,
    outOfWindowCount,
    areaHa: resolveInputsAreaHa(inputs, gUnits),
  };
};
